/**
 * AdminChurchOpsHub — Campus · Families · Follow-up console.
 *
 * Route: /admin/church-ops
 *
 * Three tabs:
 *   • Campuses — property tracker + upgrade / downgrade churches
 *   • Families — CRUD + move-family-to-church
 *   • Follow-up — stage funnel + task list + add-visitor workflow
 *
 * Every action fires against the church_workspace_v2 endpoints and
 * invalidates the shared queries so the exec dashboard stays fresh.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  ArrowLeft, Building2, Home, Users, UserPlus, PlusCircle,
  Sparkles, ArrowRightLeft, TrendingUp, CheckSquare, Clock,
  AlertCircle, X, Trash2, ExternalLink, MoveRight, Calendar,
} from 'lucide-react';
import { getToken } from '@/lib/authToken';

const API = process.env.REACT_APP_BACKEND_URL;
const authH = () => ({ Authorization: `Bearer ${getToken()}` });
const V2 = `${API}/api/admin/church-workspace`;

const STAGE_TONE = {
  visitor: 'border-amber-500/40 bg-amber-500/10 text-amber-200',
  contact_made: 'border-sky-500/40 bg-sky-500/10 text-sky-200',
  returning: 'border-violet-500/40 bg-violet-500/10 text-violet-200',
  connect_group: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-200',
  member: 'border-green-500/40 bg-green-500/10 text-green-200',
};

export default function AdminChurchOpsHub() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('campuses');
  const [selectedChurchId, setSelectedChurchId] = useState('');

  const churchesQ = useQuery({
    queryKey: ['ops-churches'],
    queryFn: async () => (await axios.get(`${API}/api/churches`, { headers: authH() })).data,
  });

  const churches = churchesQ.data?.churches || churchesQ.data?.items || churchesQ.data || [];

  // Auto-pick first church for tabs that need it
  useEffect(() => {
    if (!selectedChurchId && churches.length > 0) {
      const hq = churches.find((c) => c.level === 'hq') || churches[0];
      setSelectedChurchId(hq.id || hq._id);
    }
  }, [churches, selectedChurchId]);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="admin-church-ops-page">
      <div className="mx-auto max-w-7xl space-y-5 p-4 sm:p-6">
        {/* Header */}
        <div>
          <Button
            variant="ghost" size="sm"
            className="mb-1 text-slate-400 hover:text-slate-100"
            onClick={() => navigate(-1)} data-testid="back-btn"
          >
            <ArrowLeft className="mr-1 h-4 w-4" /> Back
          </Button>
          <h1 className="flex items-center gap-2 text-2xl font-bold">
            <Sparkles className="h-6 w-6 text-amber-400" />
            Church Ops · Campuses · Families · Follow-up
          </h1>
          <p className="text-xs text-slate-500">
            Property-driven campus tier · household records · 5-stage pastoral pipeline.
          </p>
        </div>

        {/* Tab bar */}
        <div className="flex flex-wrap items-center gap-2" data-testid="ops-tab-bar">
          {[
            { id: 'campuses', label: 'Campuses', icon: Building2 },
            { id: 'families', label: 'Families', icon: Home },
            { id: 'follow_up', label: 'Follow-up', icon: TrendingUp },
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              data-testid={`tab-${id}`}
              onClick={() => setTab(id)}
              className={`flex items-center gap-1 rounded-full border px-3 py-1 text-xs transition-colors ${
                tab === id
                  ? 'border-amber-500 bg-amber-500/15 text-amber-200'
                  : 'border-slate-800 bg-slate-950 text-slate-400 hover:border-slate-700 hover:text-slate-200'
              }`}
            >
              <Icon className="h-3 w-3" /> {label}
            </button>
          ))}
          <div className="ml-auto">
            <select
              data-testid="ops-church-picker"
              value={selectedChurchId}
              onChange={(e) => setSelectedChurchId(e.target.value)}
              className="rounded-full border border-slate-700 bg-slate-900 px-3 py-1 text-xs text-slate-200"
            >
              <option value="">— pick church —</option>
              {churches.map((c) => (
                <option key={c.id || c._id} value={c.id || c._id}>
                  {c.name} · {c.level}
                </option>
              ))}
            </select>
          </div>
        </div>

        {tab === 'campuses' && (
          <CampusTab
            churches={churches}
            selectedChurchId={selectedChurchId}
          />
        )}
        {tab === 'families' && selectedChurchId && (
          <FamiliesTab churches={churches} churchId={selectedChurchId} />
        )}
        {tab === 'follow_up' && selectedChurchId && (
          <FollowUpTab churches={churches} churchId={selectedChurchId} />
        )}
      </div>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════
// CAMPUSES TAB
// ══════════════════════════════════════════════════════════════════
function CampusTab({ churches, selectedChurchId }) {
  const qc = useQueryClient();
  const [propertyOpen, setPropertyOpen] = useState(false);

  const campusesQ = useQuery({
    queryKey: ['ops-campuses'],
    queryFn: async () => (await axios.get(`${V2}/campuses`, { headers: authH() })).data,
  });

  const propertiesQ = useQuery({
    queryKey: ['ops-properties', selectedChurchId],
    queryFn: async () => (await axios.get(
      `${V2}/churches/${selectedChurchId}/properties`, { headers: authH() },
    )).data,
    enabled: !!selectedChurchId,
  });

  const upgrade = useMutation({
    mutationFn: async () => (await axios.post(
      `${V2}/churches/${selectedChurchId}/upgrade-to-campus`, {}, { headers: authH() },
    )).data,
    onSuccess: (d) => {
      toast.success(d.already_campus ? 'Already a campus' : 'Upgraded to Campus!');
      qc.invalidateQueries({ queryKey: ['ops-campuses'] });
      qc.invalidateQueries({ queryKey: ['ops-churches'] });
    },
    onError: (e) => toast.error(`Upgrade failed: ${e?.response?.data?.detail || e.message}`),
  });

  const downgrade = useMutation({
    mutationFn: async () => (await axios.post(
      `${V2}/churches/${selectedChurchId}/downgrade-from-campus`, {}, { headers: authH() },
    )).data,
    onSuccess: () => {
      toast.success('Downgraded');
      qc.invalidateQueries({ queryKey: ['ops-campuses'] });
      qc.invalidateQueries({ queryKey: ['ops-churches'] });
    },
    onError: (e) => toast.error(`Downgrade failed: ${e?.response?.data?.detail || e.message}`),
  });

  const selectedChurch = churches.find((c) => (c.id || c._id) === selectedChurchId);
  const isCampus = selectedChurch?.level === 'campus';
  const properties = propertiesQ.data?.items || [];
  const ownedCount = properties.filter((p) => p.ownership === 'owned').length;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {/* Left: Property tracker for the selected church */}
        <Card className="border-slate-800 bg-slate-900/60" data-testid="properties-card">
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Building2 className="h-4 w-4 text-emerald-300" />
              Properties · {selectedChurch?.name || '—'}
              <Badge variant="outline" className="ml-2 border-slate-700 text-xs text-slate-400">
                {properties.length}
              </Badge>
            </CardTitle>
            <Button
              size="sm"
              variant="outline"
              className="border-emerald-500/40 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20"
              onClick={() => setPropertyOpen(true)}
              data-testid="add-property-btn"
            >
              <PlusCircle className="mr-1 h-4 w-4" /> Add Property
            </Button>
          </CardHeader>
          <CardContent className="max-h-[500px] overflow-y-auto">
            {properties.length === 0 && (
              <p className="rounded border border-dashed border-slate-800 p-3 text-center text-[11px] text-slate-500">
                No properties yet. Register at least one owned property to unlock the Campus upgrade.
              </p>
            )}
            <div className="space-y-1">
              {properties.map((p) => (
                <div
                  key={p.id}
                  data-testid={`property-row-${p.id}`}
                  className="flex items-center justify-between rounded border border-slate-800 bg-slate-950 px-3 py-2 text-sm"
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-slate-200 truncate">{p.name}</div>
                    <div className="text-[10px] text-slate-500">
                      {p.ownership} {p.city ? `· ${p.city}` : ''} {p.country ? `· ${p.country}` : ''}
                    </div>
                  </div>
                  <Badge variant="outline" className={
                    p.ownership === 'owned' ? 'border-emerald-500/40 text-emerald-300' :
                    p.ownership === 'leased' ? 'border-amber-500/40 text-amber-300' :
                    'border-slate-600 text-slate-400'
                  }>
                    {p.ownership}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Right: Campus upgrade panel */}
        <Card className="border-amber-500/30 bg-slate-900/60" data-testid="campus-upgrade-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Sparkles className="h-4 w-4 text-amber-300" />
              Campus Status
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className={`rounded-lg border p-3 ${
              isCampus
                ? 'border-amber-500/40 bg-amber-500/10'
                : 'border-slate-800 bg-slate-950'
            }`}>
              <div className="text-xs uppercase tracking-wider text-slate-500">Current level</div>
              <div className="mt-1 text-lg font-semibold text-slate-100" data-testid="current-level">
                {selectedChurch?.level || '—'}
                {isCampus && (
                  <Badge className="ml-2 border-amber-500/40 bg-amber-500/20 text-amber-200">
                    Campus
                  </Badge>
                )}
              </div>
              <div className="mt-2 text-[11px] text-slate-500">
                Owned properties: <span className="text-slate-200">{ownedCount}</span>
                {' · '}Total properties: <span className="text-slate-200">{properties.length}</span>
              </div>
            </div>

            {!isCampus && (
              <Button
                data-testid="upgrade-campus-btn"
                className="w-full bg-amber-600 hover:bg-amber-500"
                disabled={ownedCount < 1 || upgrade.isPending}
                onClick={() => upgrade.mutate()}
              >
                <Sparkles className="mr-1 h-4 w-4" />
                {upgrade.isPending ? 'Upgrading…' :
                 ownedCount < 1 ? 'Add owned property to enable' : 'Upgrade to Campus'}
              </Button>
            )}
            {isCampus && (
              <Button
                data-testid="downgrade-campus-btn"
                variant="outline"
                className="w-full border-slate-700 bg-slate-900 hover:bg-slate-800"
                disabled={downgrade.isPending}
                onClick={() => {
                  if (window.confirm('Downgrade this church from Campus?')) downgrade.mutate();
                }}
              >
                {downgrade.isPending ? 'Downgrading…' : 'Downgrade from Campus'}
              </Button>
            )}
          </CardContent>
        </Card>
      </div>

      {/* All Campuses list */}
      <Card className="border-slate-800 bg-slate-900/60" data-testid="all-campuses-card">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Building2 className="h-4 w-4 text-amber-300" /> All Campuses
            <Badge variant="outline" className="ml-2 border-slate-700 text-xs text-slate-400">
              {(campusesQ.data?.items || []).length}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {(campusesQ.data?.items || []).length === 0 ? (
            <p className="text-xs text-slate-500">No campuses yet.</p>
          ) : (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-3">
              {campusesQ.data.items.map((c) => (
                <div
                  key={c.id}
                  data-testid={`campus-tile-${c.id}`}
                  className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-3"
                >
                  <div className="font-semibold text-slate-100">{c.name}</div>
                  <div className="text-[10px] text-slate-500">{c.city}{c.country ? `, ${c.country}` : ''}</div>
                  <div className="mt-2 text-[11px] text-amber-200">
                    <Building2 className="mr-1 inline h-3 w-3" />
                    {c.property_count} propert{c.property_count === 1 ? 'y' : 'ies'}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {propertyOpen && (
        <AddPropertyDialog
          churchId={selectedChurchId}
          onClose={() => setPropertyOpen(false)}
        />
      )}
    </div>
  );
}


function AddPropertyDialog({ churchId, onClose }) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('');
  const [ownership, setOwnership] = useState('owned');
  const [valuation, setValuation] = useState('');

  const create = useMutation({
    mutationFn: async () => (await axios.post(
      `${V2}/churches/${churchId}/properties`,
      { name, address, city, country, ownership,
        valuation: valuation ? Number(valuation) : undefined },
      { headers: { ...authH(), 'Content-Type': 'application/json' } },
    )).data,
    onSuccess: () => {
      toast.success('Property registered');
      qc.invalidateQueries({ queryKey: ['ops-properties'] });
      qc.invalidateQueries({ queryKey: ['ops-campuses'] });
      onClose();
    },
    onError: (e) => toast.error(`Failed: ${e?.response?.data?.detail || e.message}`),
  });

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="property-dialog" className="bg-slate-900 border-slate-700 text-slate-100">
        <DialogHeader><DialogTitle>Register Church Property</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input data-testid="property-name" placeholder="Property name (e.g. Main Sanctuary)"
                 value={name} onChange={(e) => setName(e.target.value)}
                 className="bg-slate-950 border-slate-700" />
          <Input data-testid="property-address" placeholder="Street address"
                 value={address} onChange={(e) => setAddress(e.target.value)}
                 className="bg-slate-950 border-slate-700" />
          <div className="grid grid-cols-2 gap-2">
            <Input data-testid="property-city" placeholder="City"
                   value={city} onChange={(e) => setCity(e.target.value)}
                   className="bg-slate-950 border-slate-700" />
            <Input data-testid="property-country" placeholder="Country"
                   value={country} onChange={(e) => setCountry(e.target.value)}
                   className="bg-slate-950 border-slate-700" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <select data-testid="property-ownership" value={ownership} onChange={(e) => setOwnership(e.target.value)}
                    className="w-full rounded border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-200">
              <option value="owned">Owned</option>
              <option value="leased">Leased</option>
              <option value="donated">Donated</option>
            </select>
            <Input data-testid="property-valuation" placeholder="Valuation (optional)" type="number"
                   value={valuation} onChange={(e) => setValuation(e.target.value)}
                   className="bg-slate-950 border-slate-700" />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button data-testid="submit-property"
                    className="bg-emerald-600 hover:bg-emerald-500"
                    onClick={() => create.mutate()}
                    disabled={!name.trim() || create.isPending}>
              {create.isPending ? 'Saving…' : 'Register'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ══════════════════════════════════════════════════════════════════
// FAMILIES TAB
// ══════════════════════════════════════════════════════════════════
function FamiliesTab({ churches, churchId }) {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [detailFamily, setDetailFamily] = useState(null);

  const familiesQ = useQuery({
    queryKey: ['ops-families', churchId, search],
    queryFn: async () => (await axios.get(
      `${V2}/families?church_id=${churchId}${search ? `&search=${encodeURIComponent(search)}` : ''}`,
      { headers: authH() },
    )).data,
    enabled: !!churchId,
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          data-testid="family-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search families by name…"
          className="w-64 border-slate-700 bg-slate-900"
        />
        <Button
          data-testid="create-family-btn"
          className="bg-violet-600 hover:bg-violet-500"
          onClick={() => setCreateOpen(true)}
        >
          <PlusCircle className="mr-1 h-4 w-4" /> New Family
        </Button>
      </div>

      <Card className="border-slate-800 bg-slate-900/60" data-testid="families-card">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Home className="h-4 w-4 text-violet-300" /> Families
            <Badge variant="outline" className="ml-2 border-slate-700 text-xs text-slate-400">
              {(familiesQ.data?.items || []).length}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="max-h-[600px] overflow-y-auto">
          {familiesQ.isLoading && <p className="text-xs text-slate-500">Loading…</p>}
          {(familiesQ.data?.items || []).length === 0 && !familiesQ.isLoading && (
            <p className="text-xs text-slate-500">No families yet — create one above.</p>
          )}
          <div className="space-y-1">
            {(familiesQ.data?.items || []).map((f) => (
              <button
                type="button"
                key={f.id}
                data-testid={`family-row-${f.id}`}
                onClick={() => setDetailFamily(f)}
                className="flex w-full items-center justify-between rounded border border-slate-800 bg-slate-950 px-3 py-2 text-left text-sm hover:border-violet-500/40 hover:bg-violet-500/5"
              >
                <div>
                  <div className="font-medium text-slate-100">{f.name}</div>
                  <div className="text-[10px] text-slate-500">
                    {f.member_count} member{f.member_count === 1 ? '' : 's'}
                    {f.address ? ` · ${f.address}` : ''}
                  </div>
                </div>
                <ExternalLink className="h-3 w-3 opacity-40" />
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {createOpen && (
        <CreateFamilyDialog
          churchId={churchId}
          onClose={() => setCreateOpen(false)}
          onCreated={() => {
            qc.invalidateQueries({ queryKey: ['ops-families'] });
            setCreateOpen(false);
          }}
        />
      )}
      {detailFamily && (
        <FamilyDetailDialog
          family={detailFamily}
          churches={churches}
          onClose={() => setDetailFamily(null)}
          onChanged={() => qc.invalidateQueries({ queryKey: ['ops-families'] })}
        />
      )}
    </div>
  );
}


function CreateFamilyDialog({ churchId, onClose, onCreated }) {
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');

  const create = useMutation({
    mutationFn: async () => (await axios.post(
      `${V2}/families`,
      { name, address, phone, church_id: churchId },
      { headers: { ...authH(), 'Content-Type': 'application/json' } },
    )).data,
    onSuccess: () => {
      toast.success('Family created');
      onCreated?.();
    },
    onError: (e) => toast.error(`Failed: ${e?.response?.data?.detail || e.message}`),
  });

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="create-family-dialog" className="bg-slate-900 border-slate-700 text-slate-100">
        <DialogHeader><DialogTitle>New Family</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input data-testid="family-name" placeholder="Family name (e.g. Mubaiwa Household)"
                 value={name} onChange={(e) => setName(e.target.value)}
                 className="bg-slate-950 border-slate-700" />
          <Input data-testid="family-address" placeholder="Address" value={address}
                 onChange={(e) => setAddress(e.target.value)}
                 className="bg-slate-950 border-slate-700" />
          <Input data-testid="family-phone" placeholder="Phone" value={phone}
                 onChange={(e) => setPhone(e.target.value)}
                 className="bg-slate-950 border-slate-700" />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button data-testid="submit-family"
                    className="bg-violet-600 hover:bg-violet-500"
                    onClick={() => create.mutate()}
                    disabled={!name.trim() || create.isPending}>
              {create.isPending ? 'Creating…' : 'Create'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function FamilyDetailDialog({ family, churches, onClose, onChanged }) {
  const qc = useQueryClient();
  const [toChurchId, setToChurchId] = useState('');

  const detailQ = useQuery({
    queryKey: ['ops-family-detail', family.id],
    queryFn: async () => (await axios.get(
      `${V2}/families/${family.id}`, { headers: authH() },
    )).data,
  });

  const move = useMutation({
    mutationFn: async () => (await axios.post(
      `${V2}/families/${family.id}/move`,
      { to_church_id: toChurchId, reason: 'family move' },
      { headers: { ...authH(), 'Content-Type': 'application/json' } },
    )).data,
    onSuccess: (d) => {
      toast.success(`Moved ${d.moved_members} member(s)`);
      onChanged?.();
      onClose();
    },
    onError: (e) => toast.error(`Move failed: ${e?.response?.data?.detail || e.message}`),
  });

  const del = useMutation({
    mutationFn: async () => (await axios.delete(
      `${V2}/families/${family.id}`, { headers: authH() },
    )).data,
    onSuccess: () => { toast.success('Family deleted'); onChanged?.(); onClose(); },
    onError: (e) => toast.error(`Delete failed: ${e?.response?.data?.detail || e.message}`),
  });

  const d = detailQ.data || family;
  const members = d.members || [];
  const otherChurches = churches.filter((c) => (c.id || c._id) !== family.church_id);

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="family-detail-dialog" className="bg-slate-900 border-slate-700 text-slate-100 max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Home className="h-5 w-5 text-violet-300" /> {d.name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="text-xs text-slate-400">
            {d.address && <div>{d.address}</div>}
            {d.phone && <div>{d.phone}</div>}
            <div className="text-[11px] text-slate-500">
              {d.member_count} member{d.member_count === 1 ? '' : 's'}
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Members</div>
            {members.length === 0 ? (
              <p className="text-xs text-slate-500">No members linked yet.</p>
            ) : (
              <div className="space-y-1">
                {members.map((m) => (
                  <div key={m.id} className="flex items-center justify-between rounded border border-slate-800 bg-slate-950 px-3 py-2 text-xs">
                    <div>
                      <div className="text-slate-200">{m.first_name} {m.last_name}</div>
                      <div className="text-[10px] text-slate-500">
                        {m.family_role || 'member'} · {m.email || m.phone || '—'}
                      </div>
                    </div>
                    <Badge variant="outline" className="border-slate-700 text-slate-400 text-[10px]">
                      {m.family_role}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="rounded border border-amber-500/30 bg-amber-500/5 p-3">
            <div className="text-[10px] uppercase tracking-wider text-amber-200 mb-1 flex items-center gap-1">
              <ArrowRightLeft className="h-3 w-3" /> Move family to another church
            </div>
            <div className="flex items-center gap-2">
              <select
                data-testid="family-move-target"
                value={toChurchId}
                onChange={(e) => setToChurchId(e.target.value)}
                className="flex-1 rounded border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-200"
              >
                <option value="">— pick destination —</option>
                {otherChurches.map((c) => (
                  <option key={c.id || c._id} value={c.id || c._id}>
                    {c.name} · {c.level}
                  </option>
                ))}
              </select>
              <Button
                data-testid="family-move-submit"
                className="bg-amber-600 hover:bg-amber-500"
                onClick={() => {
                  if (window.confirm(`Move ${d.member_count} member(s) to the selected church?`)) move.mutate();
                }}
                disabled={!toChurchId || move.isPending}
              >
                <MoveRight className="mr-1 h-4 w-4" />
                {move.isPending ? 'Moving…' : 'Move'}
              </Button>
            </div>
          </div>

          <div className="flex justify-between pt-2">
            <Button
              variant="ghost"
              className="text-rose-400 hover:text-rose-300"
              onClick={() => { if (window.confirm('Delete this family? Members will be detached.')) del.mutate(); }}
              data-testid="family-delete"
              disabled={del.isPending}
            >
              <Trash2 className="mr-1 h-4 w-4" /> Delete family
            </Button>
            <Button variant="ghost" onClick={onClose}>Close</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ══════════════════════════════════════════════════════════════════
// FOLLOW-UP TAB
// ══════════════════════════════════════════════════════════════════
function FollowUpTab({ churches, churchId }) {
  const qc = useQueryClient();
  const [visitorOpen, setVisitorOpen] = useState(false);

  const summaryQ = useQuery({
    queryKey: ['ops-followup-summary', churchId],
    queryFn: async () => (await axios.get(
      `${V2}/churches/${churchId}/follow-up/summary`, { headers: authH() },
    )).data,
    refetchInterval: 30_000,
  });

  const tasksQ = useQuery({
    queryKey: ['ops-followup-tasks', churchId],
    queryFn: async () => (await axios.get(
      `${V2}/churches/${churchId}/follow-up/tasks?status=pending&limit=100`,
      { headers: authH() },
    )).data,
    refetchInterval: 30_000,
  });

  const completeTask = useMutation({
    mutationFn: async (taskId) => (await axios.put(
      `${V2}/follow-up/tasks/${taskId}`,
      { status: 'done' },
      { headers: { ...authH(), 'Content-Type': 'application/json' } },
    )).data,
    onSuccess: () => {
      toast.success('Task completed');
      qc.invalidateQueries({ queryKey: ['ops-followup-tasks'] });
      qc.invalidateQueries({ queryKey: ['ops-followup-summary'] });
    },
    onError: (e) => toast.error(`Failed: ${e?.response?.data?.detail || e.message}`),
  });

  const advanceStage = useMutation({
    mutationFn: async ({ memberId, stage }) => (await axios.post(
      `${V2}/members/${memberId}/stage`,
      { stage },
      { headers: { ...authH(), 'Content-Type': 'application/json' } },
    )).data,
    onSuccess: () => {
      toast.success('Stage advanced');
      qc.invalidateQueries({ queryKey: ['ops-followup-summary'] });
      qc.invalidateQueries({ queryKey: ['ops-followup-tasks'] });
    },
    onError: (e) => toast.error(`Failed: ${e?.response?.data?.detail || e.message}`),
  });

  const s = summaryQ.data || {};
  const stages = s.stages || [];
  const tasks = tasksQ.data?.items || [];
  const stageKeys = stages.map((st) => st.key);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Button
          data-testid="add-visitor-btn"
          className="bg-amber-600 hover:bg-amber-500"
          onClick={() => setVisitorOpen(true)}
        >
          <UserPlus className="mr-1 h-4 w-4" /> Add Visitor
        </Button>
        <div className="rounded-lg border border-rose-500/40 bg-rose-500/10 px-3 py-1 text-xs text-rose-200" data-testid="tasks-overdue-tile">
          <AlertCircle className="mr-1 inline h-3 w-3" />
          {s.tasks_overdue || 0} overdue task{s.tasks_overdue === 1 ? '' : 's'}
        </div>
        <div className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-1 text-xs text-slate-300" data-testid="tasks-pending-tile">
          <Clock className="mr-1 inline h-3 w-3" />
          {s.tasks_pending || 0} pending
        </div>
      </div>

      {/* Stage funnel */}
      <Card className="border-slate-800 bg-slate-900/60" data-testid="stage-funnel-card">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <TrendingUp className="h-4 w-4 text-emerald-300" /> Pastoral Pipeline
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-5">
            {stages.map((st) => (
              <div
                key={st.id}
                data-testid={`stage-tile-${st.key}`}
                className={`rounded-lg border p-3 ${STAGE_TONE[st.key] || 'border-slate-700 bg-slate-900'}`}
              >
                <div className="text-[10px] uppercase tracking-wider opacity-80">{st.name}</div>
                <div className="mt-1 text-2xl font-semibold">{st.member_count}</div>
                <div className="text-[10px] opacity-70">{st.description}</div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Task list */}
      <Card className="border-slate-800 bg-slate-900/60" data-testid="tasks-card">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <CheckSquare className="h-4 w-4 text-sky-300" /> Pending Follow-up Tasks
            <Badge variant="outline" className="ml-2 border-slate-700 text-xs text-slate-400">
              {tasks.length}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="max-h-[500px] overflow-y-auto">
          {tasks.length === 0 && <p className="text-xs text-slate-500">No pending tasks — everyone is followed up.</p>}
          <div className="space-y-1">
            {tasks.map((t) => (
              <div
                key={t.id}
                data-testid={`task-row-${t.id}`}
                className={`flex items-center justify-between rounded border px-3 py-2 text-sm ${
                  t.overdue
                    ? 'border-rose-500/40 bg-rose-500/5'
                    : 'border-slate-800 bg-slate-950'
                }`}
              >
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-slate-100 truncate">
                    {t.title}
                    {t.overdue && (
                      <Badge className="ml-2 border-rose-500/40 bg-rose-500/20 text-rose-200 text-[10px]">
                        overdue
                      </Badge>
                    )}
                  </div>
                  <div className="text-[10px] text-slate-500">
                    {t.member?.name || 'Member'} · stage {t.member?.follow_up_stage || '—'}
                    · due {t.due_at ? new Date(t.due_at).toLocaleDateString() : '—'}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <select
                    data-testid={`stage-picker-${t.id}`}
                    onChange={(e) => {
                      if (e.target.value) advanceStage.mutate({
                        memberId: t.member_id, stage: e.target.value,
                      });
                      e.target.value = '';
                    }}
                    defaultValue=""
                    className="rounded border border-slate-700 bg-slate-950 px-2 py-1 text-[10px] text-slate-300"
                  >
                    <option value="">→ stage</option>
                    {stageKeys.map((k) => <option key={k} value={k}>{k}</option>)}
                  </select>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => completeTask.mutate(t.id)}
                    data-testid={`complete-task-${t.id}`}
                    className="h-6 px-2 text-emerald-400 hover:text-emerald-200"
                  >
                    <CheckSquare className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {visitorOpen && (
        <AddVisitorDialog
          churchId={churchId}
          onClose={() => setVisitorOpen(false)}
          onCreated={() => {
            qc.invalidateQueries({ queryKey: ['ops-followup-summary'] });
            qc.invalidateQueries({ queryKey: ['ops-followup-tasks'] });
            setVisitorOpen(false);
          }}
        />
      )}
    </div>
  );
}


function AddVisitorDialog({ churchId, onClose, onCreated }) {
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [referrer, setReferrer] = useState('');
  const [notes, setNotes] = useState('');

  const create = useMutation({
    mutationFn: async () => (await axios.post(
      `${V2}/visitors`,
      { church_id: churchId, first_name: first, last_name: last, email, phone, referrer, notes },
      { headers: { ...authH(), 'Content-Type': 'application/json' } },
    )).data,
    onSuccess: (d) => {
      toast.success(`Visitor added · follow-up task due ${new Date(d.task_due_at).toLocaleDateString()}`);
      onCreated?.();
    },
    onError: (e) => toast.error(`Failed: ${e?.response?.data?.detail || e.message}`),
  });

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="visitor-dialog" className="bg-slate-900 border-slate-700 text-slate-100 max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-amber-300" /> Add Visitor
          </DialogTitle>
        </DialogHeader>
        <div className="rounded border border-sky-500/30 bg-sky-500/5 px-3 py-2 text-xs text-sky-200 mb-3">
          A follow-up task auto-schedules 3 days from today. You can advance the visitor through the 5-stage pipeline as they engage.
        </div>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <Input data-testid="visitor-first" placeholder="First name" value={first}
                   onChange={(e) => setFirst(e.target.value)}
                   className="bg-slate-950 border-slate-700" />
            <Input data-testid="visitor-last" placeholder="Last name (optional)" value={last}
                   onChange={(e) => setLast(e.target.value)}
                   className="bg-slate-950 border-slate-700" />
          </div>
          <Input data-testid="visitor-email" placeholder="Email" value={email}
                 onChange={(e) => setEmail(e.target.value)}
                 className="bg-slate-950 border-slate-700" />
          <Input data-testid="visitor-phone" placeholder="Phone" value={phone}
                 onChange={(e) => setPhone(e.target.value)}
                 className="bg-slate-950 border-slate-700" />
          <Input data-testid="visitor-referrer" placeholder="Invited by / Referrer" value={referrer}
                 onChange={(e) => setReferrer(e.target.value)}
                 className="bg-slate-950 border-slate-700" />
          <Textarea data-testid="visitor-notes" placeholder="Notes" value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="bg-slate-950 border-slate-700 min-h-[60px]" />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button data-testid="submit-visitor"
                    className="bg-amber-600 hover:bg-amber-500"
                    onClick={() => create.mutate()}
                    disabled={!first.trim() || create.isPending}>
              {create.isPending ? 'Saving…' : 'Save Visitor'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
